import AuthorsManager from '@/components/admin/AuthorsManager'
import { Area, Field, Panel, ReadOnlyBanner, UploadHint } from '@/components/admin/Field'
import NewsBannersManager from '@/components/admin/NewsBannersManager'
import NewsManager from '@/components/admin/NewsManager'
import Logo from '@/components/Logo'
import { useAuth } from '@/hooks/useAuth'
import { useNews } from '@/hooks/useNews'
import { useNewsBanners } from '@/hooks/useNewsBanners'
import { useRadioData } from '@/hooks/useRadioData'
import { maskSecret } from '@/lib/adminAuth'
import { lumi } from '@/lib/lumi'
import { detectPixKeyType } from '@/lib/pix'
import { sortSchedule } from '@/lib/time'
import { uploadImage } from '@/lib/upload'
import type { Banner, Partner, RadioSettings, SocialLink, TeamMember } from '@/types/radio'
import { WEEK_DAYS } from '@/types/radio'
import { useHead } from '@unhead/react'
import {ImagePlus, LogOut, Save, Trash2} from 'lucide-react'
import { FormEvent, useEffect, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { Link, Navigate } from 'react-router-dom'

const TABS = [
  { id: 'player', label: 'Player e rádio' },
  { id: 'noticias', label: 'Notícias' },
  { id: 'newsBanners', label: 'Banners de notícias' },
  { id: 'equipe', label: 'Equipa' },
  { id: 'banners', label: 'Publicidade' },
  { id: 'social', label: 'Redes sociais' },
  { id: 'parceiros', label: 'Parceiros' },
  { id: 'autores', label: 'Autores' },
] as const

type TabId = (typeof TABS)[number]['id']

function nowIso() {
  return new Date().toISOString()
}

function Admin() {
  const { loggedIn, adminName, canEditStation, isAuthor, logout } = useAuth()
  const { settings, team, banners, social, partners, loading, refresh } = useRadioData()
  const { articles, refresh: refreshNews } = useNews()
  const { banners: newsBanners, refresh: refreshNewsBanners } = useNewsBanners()
  const [tab, setTab] = useState<TabId>(isAuthor ? 'noticias' : 'player')
  const visibleTabs = TABS.filter((item) => item.id !== 'autores' || canEditStation)

  useHead({
    title: 'Painel — Bonita FM',
    htmlAttrs: { lang: 'pt-BR' },
  })

  if (!loggedIn) return <Navigate to="/login" replace />

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <Toaster position="top-right" />
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-3 py-3 sm:px-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <Logo className="h-10 sm:h-[52px]" />
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 sm:text-xs">Painel administrativo</p>
              <p className="truncate text-sm font-semibold">
                {isAuthor ? `Autor @${adminName}` : 'Gerir conteúdos da rádio'}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Link to="/" className="rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold sm:px-4 sm:text-sm">
              Ver site
            </Link>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-[#0b2ea8] px-3 py-2 text-xs font-bold text-white sm:px-4 sm:text-sm"
            >
              <LogOut size={16} /> Sair
            </button>
          </div>
        </div>
        <div className="no-scrollbar mx-auto flex max-w-6xl gap-2 overflow-x-auto px-3 pb-3 sm:px-4">
          {visibleTabs.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`shrink-0 whitespace-nowrap rounded-full px-3 py-2 text-xs font-bold sm:px-4 sm:text-sm ${
                tab === item.id ? 'bg-[#ffd21f] text-[#0b2ea8]' : 'bg-slate-100 text-slate-600'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-5 px-3 py-4 sm:px-4 sm:py-6">
        {loading ? (
          <div className="space-y-3">
            <div className="h-28 animate-pulse rounded-2xl bg-white" />
            <div className="h-52 animate-pulse rounded-2xl bg-white" />
          </div>
        ) : null}
        {tab === 'player' ? <SettingsForm settings={settings} onSaved={refresh} readOnly={!canEditStation} /> : null}
        {tab === 'noticias' ? <NewsManager articles={articles} authorName={adminName} isAuthor={isAuthor} onSaved={refreshNews} /> : null}
        {tab === 'newsBanners' ? <NewsBannersManager banners={newsBanners} onSaved={refreshNewsBanners} readOnly={!canEditStation} /> : null}
        {tab === 'equipe' ? <TeamManager team={team} onSaved={refresh} readOnly={!canEditStation} /> : null}
        {tab === 'banners' ? <BannerManager banners={banners} settings={settings} onSaved={refresh} readOnly={!canEditStation} /> : null}
        {tab === 'social' ? <SocialManager social={social} onSaved={refresh} readOnly={!canEditStation} /> : null}
        {tab === 'parceiros' ? <PartnerManager partners={partners} onSaved={refresh} readOnly={!canEditStation} /> : null}
        {tab === 'autores' && canEditStation ? <AuthorsManager /> : null}
      </main>
    </div>
  )
}

function SettingsForm({ settings, onSaved, readOnly = false }: { settings: RadioSettings | null; onSaved: () => Promise<void>; readOnly?: boolean }) {
  const [form, setForm] = useState<Partial<RadioSettings>>(settings || {})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setForm(settings || {})
  }, [settings])

  const set = (key: keyof RadioSettings, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const onUpload = async (file: File | undefined, key: keyof RadioSettings) => {
    if (!file) return
    try {
      const url = await uploadImage(file)
      set(key, url)
      toast.success('Imagem enviada.')
    } catch (err) {
      console.error(err)
      toast.error(err instanceof Error ? err.message : 'Falha no envio da imagem.')
    }
  }

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (readOnly) return
    setSaving(true)
    try {
      const payload = {
        stationName: form.stationName || 'Bonita FM',
        slogan: form.slogan || 'A rádio da nossa gente!',
        city: form.city || '',
        frequency: form.frequency || '',
        description: form.description || '',
        hours: form.hours || '',
        streamUrl: form.streamUrl || '',
        nowPlayingTitle: form.nowPlayingTitle || '',
        nowPlayingHost: form.nowPlayingHost || '',
        nowPlayingPhoto: form.nowPlayingPhoto || '',
        logoUrl: form.logoUrl || '',
        iconUrl: form.iconUrl || '',
        seoTitle: form.seoTitle || '',
        seoDescription: form.seoDescription || '',
        whatsapp: form.whatsapp || '',
        address: form.address || '',
        playStoreUrl: form.playStoreUrl || '',
        appStoreUrl: form.appStoreUrl || '',
        cityImageUrl: form.cityImageUrl || '',
        pixKey: form.pixKey || '',
        pixKeyType: form.pixKeyType || detectPixKeyType(form.pixKey || ''),
        pixBeneficiaryName: form.pixBeneficiaryName || form.stationName || 'Bonita FM',
        pixBeneficiaryCity: form.pixBeneficiaryCity || form.city || '',
        pixDocument: form.pixDocument || '',
        pixMessage: form.pixMessage || 'Doacao Bonita FM',
        bannerRotation: form.bannerRotation || 'sequential',
        updatedAt: nowIso(),
      }
      if (settings?._id) {
        await lumi.entities.radio_settings.update(settings._id, payload)
      } else {
        await lumi.entities.radio_settings.create({
          ...payload,
          creator: 'admin',
          createdAt: nowIso(),
        })
      }
      toast.success('Configurações guardadas.')
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível guardar as configurações.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={save} className="grid gap-5">
      {readOnly ? <ReadOnlyBanner /> : null}
      <Panel title="Player ao vivo e stream">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Field label="Link da radios.net ou stream direto" value={readOnly ? maskSecret(form.streamUrl) : (form.streamUrl || '')} onChange={(e) => set('streamUrl', e.target.value)} placeholder="https://www.radios.net/radio/sua-radio ou http://servidor:8000/stream" disabled={readOnly} readOnly={readOnly} />
            <p className="mt-1 text-xs text-slate-500">Cole a página da rádio na radios.net ou o endereço direto do áudio. O player tenta extrair o stream e tocar a música ao vivo. O nome, a foto e o programa ao lado do player vêm da programação cadastrada (horário atual).</p>
          </div>
        </div>
      </Panel>
      <Panel title="Identidade, SEO e funcionamento">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Nome da rádio" value={form.stationName || ''} onChange={(e) => set('stationName', e.target.value)} disabled={readOnly} readOnly={readOnly} />
          <Field label="Frequência" value={form.frequency || ''} onChange={(e) => set('frequency', e.target.value)} disabled={readOnly} readOnly={readOnly} />
          <Field label="Slogan" value={form.slogan || ''} onChange={(e) => set('slogan', e.target.value)} disabled={readOnly} readOnly={readOnly} />
          <Field label="Cidade" value={form.city || ''} onChange={(e) => set('city', e.target.value)} disabled={readOnly} readOnly={readOnly} />
          <Field label="Horário de funcionamento" value={form.hours || ''} onChange={(e) => set('hours', e.target.value)} disabled={readOnly} readOnly={readOnly} />
          <Field label="WhatsApp" value={readOnly ? maskSecret(form.whatsapp) : (form.whatsapp || '')} onChange={(e) => set('whatsapp', e.target.value)} disabled={readOnly} readOnly={readOnly} />
          <Field label="Morada" value={readOnly ? maskSecret(form.address) : (form.address || '')} onChange={(e) => set('address', e.target.value)} disabled={readOnly} readOnly={readOnly} />
          <Field label="Título SEO" value={form.seoTitle || ''} onChange={(e) => set('seoTitle', e.target.value)} disabled={readOnly} readOnly={readOnly} />
          <div className="md:col-span-2">
            <Area label="Descrição da rádio" rows={4} value={form.description || ''} onChange={(e) => set('description', e.target.value)} disabled={readOnly} readOnly={readOnly} />
          </div>
          <div className="md:col-span-2">
            <Area label="Descrição SEO" rows={3} value={form.seoDescription || ''} onChange={(e) => set('seoDescription', e.target.value)} disabled={readOnly} readOnly={readOnly} />
          </div>
          {readOnly ? null : (
            <>
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-slate-700">Ícone para ecrã inicial</span>
                <input type="file" accept="image/*" onChange={(e) => void onUpload(e.target.files?.[0], 'iconUrl')} />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-slate-700">Foto da cidade</span>
                <input type="file" accept="image/*" onChange={(e) => void onUpload(e.target.files?.[0], 'cityImageUrl')} />
              </label>
            </>
          )}
        </div>
      </Panel>
      <Panel title="Doação PIX">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Field label="Chave PIX válida" value={readOnly ? maskSecret(form.pixKey) : (form.pixKey || '')} onChange={(e) => set('pixKey', e.target.value)} placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória" disabled={readOnly} readOnly={readOnly} />
            <p className="mt-1 text-xs text-slate-500">O site gera um QR Code PIX Copia e Cola sem valor definido. Quem doar escolhe o valor no banco.</p>
          </div>
          <label className="block">
            <span className="mb-1.5 block text-sm font-semibold text-slate-700">Tipo da chave</span>
            <select
              value={form.pixKeyType || detectPixKeyType(form.pixKey || '')}
              onChange={(e) => set('pixKeyType', e.target.value)}
              disabled={readOnly}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 disabled:bg-slate-100 disabled:text-slate-500"
            >
              <option value="cpf">CPF</option>
              <option value="cnpj">CNPJ</option>
              <option value="email">E-mail</option>
              <option value="phone">Telefone</option>
              <option value="evp">Chave aleatória</option>
            </select>
          </label>
          <Field label="Nome do beneficiário" value={form.pixBeneficiaryName || ''} onChange={(e) => set('pixBeneficiaryName', e.target.value)} disabled={readOnly} readOnly={readOnly} />
          <Field label="Cidade do beneficiário" value={form.pixBeneficiaryCity || ''} onChange={(e) => set('pixBeneficiaryCity', e.target.value)} placeholder="São José do Seridó" disabled={readOnly} readOnly={readOnly} />
          <Field label="CPF ou CNPJ para exibir" value={readOnly ? maskSecret(form.pixDocument) : (form.pixDocument || '')} onChange={(e) => set('pixDocument', e.target.value)} disabled={readOnly} readOnly={readOnly} />
          <div className="md:col-span-2">
            <Field label="Mensagem no PIX (opcional)" value={form.pixMessage || ''} onChange={(e) => set('pixMessage', e.target.value)} placeholder="Doacao Bonita FM" disabled={readOnly} readOnly={readOnly} />
          </div>
        </div>
      </Panel>
      {readOnly ? null : (
        <button disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#0b2ea8] px-5 py-3 font-bold text-white disabled:opacity-60">
          <Save size={16} /> {saving ? 'A guardar...' : 'Guardar configurações'}
        </button>
      )}
    </form>
  )
}

function TeamManager({ team, onSaved, readOnly = false }: { team: TeamMember[]; onSaved: () => Promise<void>; readOnly?: boolean }) {
  const empty: Partial<TeamMember> = {
    name: '',
    programName: '',
    photoUrl: '',
    days: ['SEG', 'TER', 'QUA', 'QUI', 'SEX'],
    startTime: '06:00',
    endTime: '09:00',
    icon: 'sun',
    order: (team?.length || 0) + 1,
    active: true,
  }
  const [form, setForm] = useState<Partial<TeamMember>>(empty)
  const [editingId, setEditingId] = useState<string>('')

  const toggleDay = (day: string) => {
    const days = form.days || []
    setForm({
      ...form,
      days: days.includes(day) ? days.filter((item) => item !== day) : [...days, day],
    })
  }

  const onUpload = async (file?: File) => {
    if (!file) return
    try {
      const url = await uploadImage(file)
      setForm((prev) => ({ ...prev, photoUrl: url }))
      toast.success('Foto enviada.')
    } catch (err) {
      console.error(err)
      toast.error(err instanceof Error ? err.message : 'Falha no envio da foto.')
    }
  }

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (readOnly) return
    try {
      const payload = {
        name: form.name || '',
        programName: form.programName || '',
        photoUrl: form.photoUrl || '',
        days: form.days || [],
        startTime: form.startTime || '00:00',
        endTime: form.endTime || '00:00',
        icon: form.icon || 'sun',
        order: Number(form.order || 1),
        active: form.active !== false,
        updatedAt: nowIso(),
      }
      if (editingId) {
        await lumi.entities.team_members.update(editingId, payload)
      } else {
        await lumi.entities.team_members.create({
          ...payload,
          creator: 'admin',
          createdAt: nowIso(),
        })
      }
      toast.success('Locutor guardado.')
      setForm(empty)
      setEditingId('')
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível guardar o locutor.')
    }
  }

  const remove = async (id?: string) => {
    if (!id || readOnly) return
    try {
      await lumi.entities.team_members.delete(id)
      toast.success('Locutor removido.')
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível remover.')
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_1fr]">
      {readOnly ? <div className="lg:col-span-2"><ReadOnlyBanner /></div> : null}
      {readOnly ? null : (
        <Panel title={editingId ? 'Editar locutor' : 'Cadastrar locutor'}>
          <form onSubmit={save} className="grid gap-3">
            <Field label="Nome do locutor" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <Field label="Nome do programa" value={form.programName || ''} onChange={(e) => setForm({ ...form, programName: e.target.value })} required />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Início" type="time" value={form.startTime || ''} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
              <Field label="Fim" type="time" value={form.endTime || ''} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
            </div>
            <div>
              <p className="mb-1.5 text-sm font-semibold text-slate-700">Dias do programa</p>
              <div className="flex flex-wrap gap-2">
                {WEEK_DAYS.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`rounded-full px-3 py-1 text-xs font-bold ${(form.days || []).includes(day) ? 'bg-[#0b2ea8] text-white' : 'bg-slate-100'}`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="mb-1.5 inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                <ImagePlus size={16} /> Foto por upload
              </span>
              <input type="file" accept="image/*" onChange={(e) => void onUpload(e.target.files?.[0])} />
              <UploadHint size="800 × 800 px" ratio="1:1 (quadrado)" tip="Recorte o rosto ao centro. A foto preenche o cartão da equipa." />
              {form.photoUrl ? <img src={form.photoUrl} alt="" className="mt-2 h-24 w-24 rounded-xl object-cover" /> : null}
              <p className="mt-1 text-xs text-slate-500">A foto fica guardada no cadastro do locutor, para não desaparecer ao partilhar o projeto na comunidade.</p>
            </label>
            <Field label="Ordem" type="number" value={String(form.order || 1)} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} />
            <label className="inline-flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Visível no site
            </label>
            <button className="rounded-full bg-[#0b2ea8] py-3 font-bold text-white">Guardar locutor</button>
          </form>
        </Panel>
      )}
      <Panel title="Equipa cadastrada">
        <div className="space-y-3">
          {sortSchedule(team).map((item) => (
            <article key={item._id} className="flex flex-col gap-3 rounded-xl border border-slate-200 p-3 sm:flex-row sm:items-center">
              <img src={item.photoUrl || ''} alt={item.name} className="h-14 w-14 rounded-xl object-cover bg-slate-100" />
              <div className="min-w-0 flex-1">
                <p className="font-bold">{item.name}</p>
                <p className="text-sm text-slate-500">{item.programName} · {item.startTime}–{item.endTime}</p>
              </div>
              {readOnly ? null : (
                <div className="flex items-center gap-3">
                  <button type="button" className="text-sm font-bold text-[#0b2ea8]" onClick={() => { setForm(item); setEditingId(item._id || '') }}>Editar</button>
                  <button type="button" onClick={() => void remove(item._id)} className="text-red-500"><Trash2 size={16} /></button>
                </div>
              )}
            </article>
          ))}
        </div>
      </Panel>
    </div>
  )
}

function BannerManager({ banners, settings, onSaved, readOnly = false }: { banners: Banner[]; settings: RadioSettings | null; onSaved: () => Promise<void>; readOnly?: boolean }) {
  const emptyBanner: Partial<Banner> = { title: '', subtitle: '', imageUrl: '', linkUrl: '', order: banners.length + 1, durationSeconds: 15, weight: 1, active: true }
  const [form, setForm] = useState<Partial<Banner>>(emptyBanner)
  const [editingId, setEditingId] = useState('')
  const [rotation, setRotation] = useState(settings?.bannerRotation || 'sequential')

  useEffect(() => {
    setRotation(settings?.bannerRotation || 'sequential')
  }, [settings?.bannerRotation])

  const saveRotation = async (value: string) => {
    setRotation(value)
    if (readOnly) return
    if (!settings?._id) {
      toast.error('Guarde as configurações da rádio primeiro.')
      return
    }
    try {
      await lumi.entities.radio_settings.update(settings._id, {
        bannerRotation: value,
        updatedAt: nowIso(),
      })
      toast.success('Rotação da publicidade atualizada.')
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível guardar a rotação.')
    }
  }

  const onUpload = async (file?: File) => {
    if (!file) return
    try {
      const url = await uploadImage(file)
      setForm((prev) => ({ ...prev, imageUrl: url }))
      toast.success('Banner enviado.')
    } catch (err) {
      console.error(err)
      toast.error(err instanceof Error ? err.message : 'Falha no envio do banner.')
    }
  }

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (readOnly) return
    try {
      const payload = {
        title: form.title || '',
        subtitle: form.subtitle || '',
        imageUrl: form.imageUrl || '',
        linkUrl: form.linkUrl || '',
        order: Number(form.order || 1),
        durationSeconds: Math.max(3, Number(form.durationSeconds || 15)),
        weight: Math.max(1, Number(form.weight || 1)),
        active: form.active !== false,
        updatedAt: nowIso(),
      }
      if (editingId) {
        await lumi.entities.banners.update(editingId, payload)
      } else {
        await lumi.entities.banners.create({ ...payload, creator: 'admin', createdAt: nowIso() })
      }
      toast.success('Publicidade guardada.')
      setForm({ ...emptyBanner, order: banners.length + 1 })
      setEditingId('')
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível guardar o banner.')
    }
  }

  const remove = async (id?: string) => {
    if (!id || readOnly) return
    try {
      await lumi.entities.banners.delete(id)
      toast.success('Publicidade removida.')
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível remover.')
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {readOnly ? <div className="lg:col-span-2"><ReadOnlyBanner /></div> : null}
      {readOnly ? null : (
        <Panel title="Cadastrar publicidade">
          <form onSubmit={save} className="grid gap-3">
            <Field label="Título" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            <Field label="Subtítulo" value={form.subtitle || ''} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
            <Field label="Link opcional" value={form.linkUrl || ''} onChange={(e) => setForm({ ...form, linkUrl: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tempo no ar (segundos)" type="number" value={String(form.durationSeconds || 15)} onChange={(e) => setForm({ ...form, durationSeconds: Number(e.target.value) })} />
              <Field label="Peso (aparece mais vezes)" type="number" value={String(form.weight || 1)} onChange={(e) => setForm({ ...form, weight: Number(e.target.value) })} />
            </div>
            <Field label="Ordem" type="number" value={String(form.order || 1)} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} />
            <label className="inline-flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Visível no site
            </label>
            <label className="block">
              <span className="mb-1.5 text-sm font-semibold text-slate-700">Imagem por upload</span>
              <input type="file" accept="image/*" onChange={(e) => void onUpload(e.target.files?.[0])} />
              <UploadHint size="1920 × 640 px" ratio="3:1" tip="Espaço de publicidade em largura total. Evite texto pequeno nas extremidades." />
              {form.imageUrl ? <img src={form.imageUrl} alt="" className="mt-2 h-28 w-full rounded-xl object-cover" /> : null}
            </label>
            <button className="rounded-full bg-[#0b2ea8] py-3 font-bold text-white">Guardar publicidade</button>
          </form>
        </Panel>
      )}
      <Panel title="Publicidades atuais">
        {readOnly ? null : (
          <div className="mb-4 rounded-xl border border-slate-200 p-3">
            <p className="mb-2 text-sm font-semibold text-slate-700">Como os anúncios giram</p>
            <select
              value={rotation}
              onChange={(e) => void saveRotation(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5"
            >
              <option value="sequential">Em ordem (1, 2, 3...)</option>
              <option value="random">Aleatório, respeitando o peso</option>
            </select>
            <p className="mt-2 text-xs text-slate-500">No modo aleatório, um anúncio com peso 3 aparece cerca de 3 vezes mais do que um com peso 1.</p>
          </div>
        )}
        <div className="space-y-3">
          {banners.map((item) => (
            <article key={item._id} className="overflow-hidden rounded-xl border border-slate-200">
              <img src={item.imageUrl} alt={item.title} className="h-28 w-full object-cover" />
              <div className="flex items-center justify-between p-3">
                <div>
                  <p className="font-bold">{item.title}</p>
                  <p className="text-xs text-slate-500">{item.durationSeconds || 15}s · peso {item.weight || 1}</p>
                </div>
                {readOnly ? null : (
                  <div className="flex gap-3">
                    <button type="button" className="text-sm font-bold text-[#0b2ea8]" onClick={() => { setForm(item); setEditingId(item._id || '') }}>Editar</button>
                    <button type="button" onClick={() => void remove(item._id)} className="text-red-500"><Trash2 size={16} /></button>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </Panel>
    </div>
  )
}

function SocialManager({ social, onSaved, readOnly = false }: { social: SocialLink[]; onSaved: () => Promise<void>; readOnly?: boolean }) {
  const saveOne = async (item: SocialLink) => {
    if (!item._id || readOnly) return
    try {
      await lumi.entities.social_links.update(item._id, {
        platform: item.platform,
        url: item.url,
        order: item.order,
        active: item.active,
        updatedAt: nowIso(),
      })
      toast.success('Rede social atualizada.')
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível atualizar.')
    }
  }

  const add = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (readOnly) return
    const data = new FormData(event.currentTarget)
    try {
      await lumi.entities.social_links.create({
        platform: String(data.get('platform') || ''),
        url: String(data.get('url') || ''),
        order: social.length + 1,
        active: true,
        creator: 'admin',
        createdAt: nowIso(),
        updatedAt: nowIso(),
      })
      toast.success('Rede social adicionada.')
      event.currentTarget.reset()
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível adicionar.')
    }
  }

  return (
    <Panel title="Redes sociais">
      {readOnly ? <ReadOnlyBanner /> : null}
      {readOnly ? null : (
        <form onSubmit={add} className="mb-5 grid gap-3 sm:grid-cols-[140px_1fr] lg:grid-cols-[160px_1fr_auto]">
          <Field label="Plataforma" name="platform" placeholder="instagram" required />
          <Field label="Link" name="url" placeholder="https://" required />
          <button className="w-full rounded-full bg-[#0b2ea8] px-5 py-2.5 font-bold text-white lg:w-auto lg:self-end">Adicionar</button>
        </form>
      )}
      <div className="space-y-3">
        {social.map((item) => (
          <SocialRow key={item._id} item={item} onSave={saveOne} readOnly={readOnly} />
        ))}
      </div>
    </Panel>
  )
}

function SocialRow({ item, onSave, readOnly = false }: { item: SocialLink; onSave: (item: SocialLink) => Promise<void>; readOnly?: boolean }) {
  const [url, setUrl] = useState(item.url)
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-slate-200 p-3 md:flex-row md:items-center">
      <p className="w-28 font-bold capitalize">{item.platform}</p>
      <input
        value={readOnly ? maskSecret(url) : url}
        onChange={(e) => setUrl(e.target.value)}
        disabled={readOnly}
        readOnly={readOnly}
        className="flex-1 rounded-xl border border-slate-200 px-3 py-2 disabled:bg-slate-100 disabled:text-slate-500"
      />
      {readOnly ? null : (
        <button type="button" onClick={() => void onSave({ ...item, url })} className="rounded-full bg-[#0b2ea8] px-4 py-2 text-sm font-bold text-white">
          Guardar
        </button>
      )}
    </div>
  )
}

function PartnerManager({ partners, onSaved, readOnly = false }: { partners: Partner[]; onSaved: () => Promise<void>; readOnly?: boolean }) {
  const add = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (readOnly) return
    const data = new FormData(event.currentTarget)
    try {
      await lumi.entities.partners.create({
        name: String(data.get('name') || ''),
        url: String(data.get('url') || ''),
        logoUrl: '',
        order: partners.length + 1,
        active: true,
        creator: 'admin',
        createdAt: nowIso(),
        updatedAt: nowIso(),
      })
      toast.success('Parceiro adicionado.')
      event.currentTarget.reset()
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível adicionar o parceiro.')
    }
  }

  const remove = async (id?: string) => {
    if (!id || readOnly) return
    try {
      await lumi.entities.partners.delete(id)
      toast.success('Parceiro removido.')
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível remover.')
    }
  }

  return (
    <Panel title="Links de parceiros">
      {readOnly ? <ReadOnlyBanner /> : null}
      {readOnly ? null : (
        <form onSubmit={add} className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto]">
          <Field label="Nome" name="name" required />
          <Field label="Link" name="url" required />
          <button className="w-full rounded-full bg-[#0b2ea8] px-5 py-2.5 font-bold text-white sm:col-span-2 lg:col-span-1 lg:w-auto lg:self-end">Adicionar</button>
        </form>
      )}
      <div className="space-y-2">
        {partners.map((item) => (
          <div key={item._id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3">
            <div className="min-w-0">
              <p className="font-bold">{item.name}</p>
              <p className="break-all text-sm text-slate-500">{readOnly ? maskSecret(item.url) : item.url}</p>
            </div>
            {readOnly ? null : <button type="button" onClick={() => void remove(item._id)} className="text-red-500"><Trash2 size={16} /></button>}
          </div>
        ))}
      </div>
    </Panel>
  )
}

export default Admin
